import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { Card as NextUICard, Button as NextUIButton, CardBody as NextUICardBody } from '@nextui-org/react'; 
import { Checkbox } from '@nextui-org/react'; 
import { Dropdown, DropdownTrigger} from "@nextui-org/react"; // Import necessary NextUI components
import { tradesConfig } from '../Trades/tradesconfig';
import { DropdownItem as NextUIDropdownItem } from '@nextui-org/react';
import { DropdownMenu as NextUIDropDownMenu } from '@nextui-org/react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

import { FiRefreshCw } from "react-icons/fi"; // Import refresh icon
import { IoMdArrowRoundBack } from "react-icons/io";



const backUrl = process.env.REACT_APP_BACK_URL;

const BackButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px; /* Space between arrow and text */
  font-size: 14px; /* Smaller font */
  color: blue;
  cursor: pointer;
  margin-top: 8px; /* Space below Next button */
  font-weight: normal; /* Not bold */
  transition: color 0.2s ease;

  &:hover {
    color: #0033cc;
  }
`;

const ArrowIcon = styled(IoMdArrowRoundBack)`
  font-size: 16px; /* Adjust arrow size */
`;


// Keyframe for slide-in animation
const slideInFromLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-100vw);
  }
  70% {
    opacity: 1;
    transform: translateX(10px);
  }
  100% {
    transform: translateX(0);
  }
`;

// Keyframe for slide-in from right
const slideInFromRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;


//////////////////////////////

// Color constants for the design
const primaryBlue = '#4A90E2';
const white = '#FFFFFF';
const lightGray = '#E6E9ED';


const StyledDropdownItem = styled(NextUIDropdownItem)`
  font-size: 50px; /* Increase font size */
`;

const DropdownMenu = styled(NextUIDropDownMenu)`
  margin-left: -10px;
  font-size: 140px;
`;


const Card = styled(NextUICard)`
  max-width: 450px;
  width: 100%;
  padding: 18px;
  background-color: ${white};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1), 0 0 15px rgba(253, 216, 53, 0.2);
  border-radius: 10px;
  border: 0.5px solid #0068C9;
  margin-bottom: 36px;
  animation: ${({ step }) => (step % 2 === 0 ? slideInFromRight : slideInFromLeft)} 0.5s ease;
  
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  /* Smooth height transition */
  transition: all 0.3s ease;

  /* Dynamically adjust height based on step */
  height: ${({ step }) => (step === 5 ? '600px' : 'auto')}; /* Adjust height for step 5 */

  /* Keep it visually consistent */
  overflow: hidden; /* Prevent abrupt content overflow */
`;



const CardBody = styled(NextUICardBody)`
  padding: 3px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  flex-grow: 1; /* Allows the body to take up available space */
`;


const Button = styled(NextUIButton)`
  background-color: #1e3a8a;
  color: ${white};
  border-radius: 20px; /* Slightly rounded corners for a modern feel */
  font-family: 'Poppins', sans-serif;
  font-size: 16px; /* Fixed font size */
  font-weight: 600; /* Slightly bold for readability */
  padding: 12px 24px; /* Balanced padding for better click area */
  text-align: center;
  width: 200px;
  cursor: pointer;
  border: none; /* Remove border */
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1); /* Subtle shadow for depth */
  transition: background-color 0.2s ease, transform 0.1s ease;

  /* Centering the button */
  display: block;
  margin: 16px auto; /* Center horizontally and add vertical spacing */

  &:hover {
    background-color:rgb(12, 58, 195); /* Slightly darker shade on hover */
  }

  &:active {
    transform: scale(0.98); /* Slight scale effect on click */
  }

  &:disabled {
    background-color: #c4c4c4; /* Disabled state color */
    cursor: not-allowed;
    box-shadow: none;
  }
`;




// Styled components for form elements
const InputLabel = styled.label`
  display: block;
  margin-bottom: 10px;
  color:  #0068C9;
  font-size: 16px;
  font-weight: bold;
  font-family: 'Poppins', sans-serif;
`;

const Input = styled.input`
  width: 100%;
  padding: 10.8px; /* Reduced from 12px */
  margin-bottom: 18px; /* Reduced from 20px */
  border: 1px solid ${lightGray};
  border-radius: 8px;
  font-size: 14.4px; /* Reduced from 16px */
  box-sizing: border-box;
  background-color: ${white};
`;

const Field = styled.button`
  width: 100%;
  padding: 10.8px; 
  margin-bottom: 18px; 
  border: 1px solid ${lightGray};
  border-radius: 8px;
  font-size: 14.4px; 
  box-sizing: border-box;
  background-color: ${white};
  text-align: left;
`;




const Textarea = styled.textarea`
  width: 100%;
  padding: 10.8px; /* Reduced from 12px */
  margin-bottom: 18px; /* Reduced from 20px */
  border: 1px solid ${lightGray};
  border-radius: 8px;
  font-size: 14.4px; /* Reduced from 16px */
  resize: vertical;
  box-sizing: border-box;
  background-color: ${white};
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* Two columns */
  gap: 10px 15px; /* Spacing between checkboxes */
  margin-bottom: 20px; /* Consistent margin */
  width: 100%; /* Ensure full width */
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr; /* Single column on smaller screens */
  }
`;

const StyledCheckbox = styled(Checkbox)`
  width: 100%; /* Ensures label takes full width */
  font-size: 14px; /* Slightly smaller font for better fit */
  display: flex;
  align-items: center;
`;


const ErrorMessage = styled.p`
  color: red;
  font-size: 12px;
  margin-top: -10px;
  margin-bottom: 10px;
`;

const ToggleLink = styled.p`
  color: ${primaryBlue};
  cursor: pointer;
  font-size: 14px;
  text-align: center;
  &:hover {
    text-decoration: underline;
  }
`;

const TermsLink = styled(Link)`
  color: ${primaryBlue};
  font-family: 'Poppins', sans-serif;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const InfoMessage = styled.p`
  color: red;
  font-size: 12px;
  text-align: center;
  margin-top: 5px;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  max-width: 600px;
  width: 90%;
`;

const Modal = styled.div`
  max-width: 800px;
  max-height: 90vh; /* Limit modal height to viewport */
  background-color: ${white};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  @media (max-width: 600px) {
    max-width: 95%; /* Make the modal fit better on smaller screens */
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  position: relative; /* To position the close button */
  font-size: 20px;
  font-weight: bold;
  color: ${primaryBlue};
  border-bottom: 1px solid ${lightGray};
  text-align: center;

  h2 {
    margin: 0;
    flex-grow: 1; /* Ensures the title takes up all remaining space */
  }

  @media (max-width: 600px) {
    font-size: 18px; /* Smaller font size for mobile */
  }
`;


const ModalBody = styled.div`
  flex: 1; /* Allows the body to take up remaining space */
  overflow-y: auto; /* Enable vertical scrolling */
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const JobDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr; /* Two-column layout */
  column-gap: 20px;
  row-gap: 10px;
`;

const JobDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  strong {
    color: ${primaryBlue};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 20px;
  border-top: 1px solid ${lightGray};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: #1e3a8a;
  font-size: 20px;
  cursor: pointer;
  z-index: 10;

  &:hover {
    color: #0033cc;
  }

  &:focus {
    outline: none;
  }
`;

const RefreshButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: #1e3a8a;
  font-size: 18px; /* Icon size */
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #0033cc; /* Slightly darker blue on hover */
  }

  &:focus {
    outline: none;
  }

  svg {
    animation: ${({ isRotating }) => (isRotating ? "spin 0.5s linear" : "none")}; /* Rotate animation */
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ScrollableDropdownMenu = styled(DropdownMenu)`
  max-height: 200px; /* Limit the dropdown height */
  overflow-y: auto; /* Enable vertical scrolling */
  scrollbar-width: thin; /* Thin scrollbar for modern look */
  scrollbar-color: #c4c4c4 #f8f8f8; /* Customize scrollbar color */

  /* Custom scrollbar for Webkit browsers (Chrome, Safari) */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c4c4c4;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background-color: #f8f8f8;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadButton = styled.label`
  display: inline-block;
  background-color: rgba(0, 51, 204, 0.8); /* Slight opacity */
  color: white;
  width: 140px;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: background 0.3s ease;
  margin-bottom: 10px;

  /* ✅ Left align */
  margin-left: 0;

  &:hover {
    background-color: rgba(0, 51, 204, 1);
  }

  &:active {
    transform: scale(0.97);
  }
`;




export function PJC ({ formState, setFormState, isLoggedIn = false, onSuccess, customerId })  {
    const [step, setStep] = useState(formState?.step || 1);
    const [isLoading, setIsLoading] = useState(false); 
    const [message, setMessage] = useState(""); // ✅ Initialize message state
    const [formData, setFormData] = useState(() => ({
      trade: '',
      postcode: '',
      selectedTasks: [],
      longDescription: '',
      email: '',
      name: '',
      phone: '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
      isNewUser: true,
      images: [], // ✅ Ensures images is always an array
      ...formState,  // ✅ Ensures formState values persist if available
    }));
  
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const [isRotating, setIsRotating] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const location = useLocation();
  
    useEffect(() => {
      console.log("📸 formData.images updated:", (formData.images || []).map(f => f.name)); // ✅ Fix: Ensuring images is always an array
      setFormState((prevState) => ({
          ...prevState,
          ...formData,
          images: formData.images || [],  // ✅ Fix: Ensured images persist
          selectedTasks: formData.selectedTasks || [], // ✅ Fix: Ensured selectedTasks persist
          step,
      }));
  }, [formData, step, setFormState]);
  
 const formatUKPostcode = (value = "") => {
  const raw = value.toUpperCase().replace(/\s+/g, "");

  // Accept common UK shapes (and GIR 0AA). If it doesn't look like one, don't force format.
  const looksLikeUK = /^(GIR0AA|[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2})$/.test(raw);
  if (!looksLikeUK) return value.toUpperCase();

  return `${raw.slice(0, raw.length - 3)} ${raw.slice(-3)}`;
};

  const isValidUKPostcode = (postcode) => {
    const regex = /^(GIR 0AA|[A-PR-UWYZ][A-HK-Y]?[0-9][0-9]?[A-Z]?\s?[0-9][ABD-HJLNP-UW-Z]{2})$/i;
    return regex.test(postcode.trim());
  };
  
  
  
  
  const validatePostcodeAPI = async (postcode) => {
    if (!isValidUKPostcode(postcode)) return false; // 🚀 Reject before API call
  
    try {
      const response = await fetch(`https://api.postcodes.io/postcodes/${postcode}/validate`);
      const data = await response.json();
      return data.result; // ✅ Returns true if valid, false if invalid
    } catch (error) {
      console.error("Postcode API error:", error);
      return false; // Assume invalid if API fails
    }
  };
  
  
  
  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
  
    if (type === "checkbox") {
      if (name === "termsAccepted") {
        setFormData((prevData) => ({
          ...prevData,
          termsAccepted: checked,
        }));
      } else {
        setFormData((prevData) => ({
          ...prevData,
          selectedTasks: checked
            ? [...(prevData.selectedTasks || []), value] // ✅ Fixed undefined selectedTasks
            : (prevData.selectedTasks || []).filter((task) => task !== value),
        }));
      }
    } else {
      const newValue = name === "postcode" ? value.toUpperCase() : value;
    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));

    // 🚀 Step 1: Reject instantly if format is incorrect
    if (name === "postcode") {
      if (!isValidUKPostcode(newValue)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          postcode: "Please use a valid UK Postcode",
        }));
        return;
      }

      // 🚀 Step 2: Check against API if regex is valid
      const isValid = await validatePostcodeAPI(newValue);
      if (!isValid) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          postcode: "Invalid postcode (not recognized).",
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          postcode: null, // ✅ Clear error if valid
        }));
      }
    }
  }
};


    const handleImageUpload = (e) => {
      const files = Array.from(e.target.files);
  
      if (files.length === 0) return;
  
      setFormData((prevData) => {
          const updatedImages = [...(prevData.images || []), ...files];
  
          if (updatedImages.length > 3) {
              alert("You can upload a maximum of 3 images.");
              return prevData;
          }
  
          console.log("📸 Selected Images Before:", prevData.images);
          console.log("🔥 Updated Images After:", updatedImages);
  
          return { ...prevData, images: updatedImages };
      });
  
      // 🔍 Debugging: Check if images persist after update
      setTimeout(() => {
          console.log("📝 Images after state update:", formData.images ? formData.images.map(f => f.name) : "No images in state");
      }, 500);
  };
  
  // 🔥 Remove an image
  const removeImage = (index) => {
      setFormData((prevData) => ({
          ...prevData,
          images: prevData.images.filter((_, i) => i !== index),
      }));
  };

    
  
    const validateFields = () => {
      let newErrors = {};
  
      switch (step) {
        case 1:
          if (!formData.trade) newErrors.trade = 'Please select a trade.';
          break;
          case 2:
            if (!formData.postcode) {
              newErrors.postcode = "Please enter your postcode.";
            } else if (!isValidUKPostcode(formData.postcode)) {
              newErrors.postcode = "Invalid UK postcode format.";
            } else if (errors.postcode) {
              newErrors.postcode = errors.postcode; // Keep async API validation error
            }
            break;
        case 3:
          if (formData.selectedTasks.length === 0) newErrors.selectedTasks = 'Please select at least one task.';
          break;
        case 5:
          if (!formData.email) {
            newErrors.email = 'Please enter your email.';
          } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address.';
          }
          if (!formData.name) {
            newErrors.name = 'Please enter your name.';
          }
          if (!formData.phone) {
            newErrors.phone = 'Please enter your phone number.';
          }
  
          if (formData.isNewUser) {
            if (!formData.password) {
              newErrors.password = 'Please enter a password.';
            } else if (formData.password.length < 6) {
              newErrors.password = 'Password must be at least 6 characters.';
            }
  
            if (!formData.confirmPassword) {
              newErrors.confirmPassword = 'Please confirm your password.';
            } else if (formData.password !== formData.confirmPassword) {
              newErrors.confirmPassword = 'Passwords do not match.';
            }
  
            if (!formData.termsAccepted) {
              newErrors.termsAccepted = 'You must accept the Terms and Conditions.';
            }
          }
          break;
        default:
          break;
      }
  
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

  
    const handleSubmit = async () => {
      console.log("📤 Preparing to submit job...");
  
      if (!validateFields()) return; // ✅ Ensure validation before submission
      setIsLoading(true);  // 🔥 Show loading button
  
      try {
          // 🚀 Step 1: Check if the email already exists
          const emailCheckResponse = await fetch(`${backUrl}/auth/check-email`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email: formData.email }),
          });
  
          const emailCheckData = await emailCheckResponse.json();
          
          if (emailCheckData.exists) {
              console.warn("❌ Email already in use! Redirecting to login modal.");
              
              setMessage("⚠️ Email already in use. Please log in to continue."); // ✅ Set the message
              setShowModal(true);  // 🚀 Open login modal
              setIsLoading(false);
              return;
          }
  
          // 🚀 Step 2: Proceed with job submission if email is NOT registered
          const formDataObj = new FormData();
          formDataObj.append("trade", formData.trade);
          formDataObj.append("postcode", formData.postcode);
          formDataObj.append("selectedTasks", JSON.stringify(formData.selectedTasks));
          formDataObj.append("longDescription", formData.longDescription);
          formDataObj.append("email", formData.email);
          formDataObj.append("name", formData.name);
          formDataObj.append("phone", formData.phone);
          formDataObj.append("password", formData.password);
          formDataObj.append("confirmPassword", formData.confirmPassword);
          formDataObj.append("termsAccepted", formData.termsAccepted);
          formDataObj.append("isNewUser", formData.isNewUser);
  
          // Append files if they exist
          if (formData.images && formData.images.length > 0) {
              formData.images.forEach((image) => {
                  if (image instanceof File) {
                      formDataObj.append("files", image);
                  }
              });
          }
  
          const response = await fetch(`${backUrl}/jobs/log-job`, {
              method: "POST",
              body: formDataObj,
          });
  

          const data = await response.json();
          console.log("✅ Server Response:", data);
  
          if (response.ok) {
              alert("Success!! Job logged, please verify your email via the link sent to your inbox! Please check junk email if not received within 5 minutes");
              if (onSuccess) {
  onSuccess();
} else {
  navigate("/login");
}
          } else {
              alert(data.message || "Failed to log the job.");
          }
        } catch (error) {
          console.error("❌ Error submitting job:", error);
          alert("An error occurred while submitting your job.");
      } finally {
          setIsLoading(false);  // 🔥 Hide loading button
      }
  };
  
  const handleSubmitForLoggedUsers = async () => {
    console.log("📤 Submitting job for logged-in user...");

    if (!formData.email.trim() || !formData.password.trim()) {
        alert("Please enter your email and password.");
        return;
    }

    // ✅ Ensure user is marked as registered to bypass step 5 validation
    setFormData((prevData) => ({ ...prevData, isNewUser: false }));

    const formDataObj = new FormData();
    formDataObj.append("trade", formData.trade);
    formDataObj.append("postcode", formData.postcode);
    formDataObj.append("selectedTasks", JSON.stringify(formData.selectedTasks));
    formDataObj.append("longDescription", formData.longDescription);
    formDataObj.append("email", formData.email);
    formDataObj.append("password", formData.password);
    if (customerId) {
      formDataObj.append("customerId", customerId);
    }
    

    // ✅ Include images if uploaded
    if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
            if (image instanceof File) {
                formDataObj.append("files", image);
            }
        });
    }

    try {
        const response = await fetch(`${backUrl}/jobs/log-job`, {
            method: "POST",
            body: formDataObj,
        });

        const data = await response.json();
        if (response.ok) {
            alert("Success! Job logged under your account.");
            setShowModal(false);
            if (onSuccess) {
              onSuccess(); // ✅ Use the callback
            } else {
              navigate("/customer-homepage");
            }
        } else {
            alert(data.message || "Failed to log the job.");
        }
      } catch (error) {
        console.error("❌ Error submitting job for logged-in user:", error);
        alert("An error occurred while submitting your job.");
    } finally {
        setIsLoading(false);  // 🔥 Hide loading
    }
};



    
  
    const nextStep = () => {
      if (validateFields()) {
          console.log(`✅ Moving from Step ${step} to ${step + 1}.`);
          setFormData((prevData) => ({
              ...prevData,
              images: [...(prevData.images || [])], // ✅ Ensured images persist
          }));

          if (step === 5 && formData.isNewUser) {
            handleSubmit();
        } else if (step === 5 && !formData.isNewUser) {
            handleSubmitForLoggedUsers();  // ✅ Skip step 5 validation and submit as a registered user
        }else {
              setStep(step + 1);
          }
      }
    };


  
    const taskOptions = tradesConfig || {}; // Default to an empty object if undefined
const trades = tradesConfig ? Object.keys(tradesConfig).sort() : []; // Safely handle undefined


const resetForm = () => {
  setIsRotating(true); // Start spin animation
  setTimeout(() => {
    // Reset form after animation
    setFormData({
      trade: "",
      postcode: "",
      selectedTasks: [],
      longDescription: "",
      email: "",
      name: "",
      phone: "",
      password: "",
      confirmPassword: "",
      termsAccepted: false,
      isNewUser: true,
    });
    setStep(1);
    setIsRotating(false); // Stop spin animation
  }, 500); // Match the spin animation duration
};



  return (
          <Card>

<RefreshButton onClick={resetForm} isRotating={isRotating}>
        <FiRefreshCw />
      </RefreshButton>
        <CardBody>
          {step === 1 && (
            <>
            <InputLabel>Select a trade:</InputLabel>
                    <Dropdown>
                    <DropdownTrigger>
                        <Field variant="bordered">{formData.trade || "Select.."}</Field> 
                    </DropdownTrigger>
                    <ScrollableDropdownMenu
                                      aria-label="Trade selection"
                                      selectionMode="single"
                                      selectedKeys={new Set([formData.trade])}
                                      onSelectionChange={(key) => {
                                        const selectedTrade = [...key][0];
                                        setFormData({ ...formData, trade: selectedTrade });
                                      }}
                                    >
                                      {trades.map((trade) => (
                                        <NextUIDropdownItem key={trade} value={trade}>
                                          {trade}
                                        </NextUIDropdownItem>
                                      ))}
                                    </ScrollableDropdownMenu>
                    </Dropdown>

          
            {errors.trade && <ErrorMessage>{errors.trade}</ErrorMessage>}
          
            <Button onClick={nextStep}>Next</Button>
          </>
          )}

          {step === 2 && (
            <>
              <InputLabel>Job Postcode:</InputLabel>
              <Input
                type="text"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                onBlur={(e) => {
                  const formatted = formatUKPostcode(e.target.value);
                  setFormData((prev) => ({ ...prev, postcode: formatted }));
                }}
                placeholder="e.g., EN3 7JF"
              />
              {errors.postcode && <ErrorMessage>{errors.postcode}</ErrorMessage>}
              <Button onClick={nextStep}>Next</Button>
              <BackButton onClick={() => setStep(step - 1)}>
    <ArrowIcon />
    Back
  </BackButton>
            </>
          )}

{step === 3 && (
  <>
    <InputLabel>Select tasks (you can select multiple):</InputLabel>
    <CheckboxGroup>
      {taskOptions[formData.trade]?.map((task, index) => (
        <StyledCheckbox
          key={index}
          isSelected={formData.selectedTasks.includes(task)}
          onChange={() => {
            if (formData.selectedTasks.includes(task)) {
              setFormData((prevData) => ({
                ...prevData,
                selectedTasks: prevData.selectedTasks.filter((t) => t !== task),
              }));
            } else {
              setFormData((prevData) => ({
                ...prevData,
                selectedTasks: [...prevData.selectedTasks, task],
              }));
            }
          }}
        >
          {task}
        </StyledCheckbox>
      ))}
    </CheckboxGroup>
    {errors.selectedTasks && <ErrorMessage>{errors.selectedTasks}</ErrorMessage>}
    <Button onClick={nextStep}>Next</Button>
              <BackButton onClick={() => setStep(step - 1)}>
    <ArrowIcon />
    Back
  </BackButton>
  </>
)}
          {step === 4 && (
            <>
              <InputLabel>Long description of the job (optional):</InputLabel>
              <Textarea
                name="longDescription"
                value={formData.longDescription}
                onChange={handleChange}
                placeholder="Provide additional details about the job..."
              />
              {/* 🔥 Image Upload Section */}
              <InputLabel>Upload Images (Max 3):</InputLabel>
<UploadButton htmlFor="image-upload">Upload Image</UploadButton>
<HiddenFileInput
  id="image-upload"
  type="file"
  name="file"
  accept="image/*"
  multiple
  onChange={handleImageUpload}
/>


                        {/* 🔥 Preview Uploaded Images */}
                        {formData.images.length > 0 && (
                            <div>
                                <p>Uploaded Images:</p>
                                {formData.images.map((image, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                        <img src={URL.createObjectURL(image)} alt={`Preview ${index}`} style={{ width: '100px', height: '100px', objectFit: 'cover', marginRight: '10px' }} />
                                        <button onClick={() => removeImage(index)}>Remove</button>
                                    </div>
                                ))}
                            </div>
                        )}

              <Button onClick={nextStep}>Next</Button>
              <BackButton onClick={() => setStep(step - 1)}>
    <ArrowIcon />
    Back
  </BackButton>
            </>
          )}

{step === 5 && (
  <>
    {/* Already Registered Modal Trigger */}
    <ToggleLink onClick={() => setShowModal(true)}>Already Registered?</ToggleLink>

    {/* Job Details Modal */}
    {showModal && (
  <ModalBackdrop onClick={() => setShowModal(false)}>
    <ModalWrapper onClick={(e) => e.stopPropagation()}>
      <Modal>
        <ModalHeader>
          <h2>Log Job to Your Account</h2>
          <Button onClick={() => setShowModal(false)} style={{ fontSize: '16px', marginLeft: 'auto' }}>
            ✕
          </Button>
        </ModalHeader>
        <ModalBody>
        {message && <InfoMessage>{message}</InfoMessage>} {/* 🚀 Show Error Message */}
          <h4>Job Details:</h4>
          <JobDetails>
            <JobDetailItem>
              <strong>Trade:</strong>
              <span>{formData.trade}</span>
            </JobDetailItem>
            <JobDetailItem>
              <strong>Postcode:</strong>
              <span>{formData.postcode}</span>
            </JobDetailItem>
            <JobDetailItem>
              <strong>Tasks:</strong>
              <span>{formData.selectedTasks.join(', ')}</span>
            </JobDetailItem>
            <JobDetailItem>
              <strong>Description:</strong>
              <span>{formData.longDescription || 'N/A'}</span>
            </JobDetailItem>
          </JobDetails>

          <h4>Login to Log Job:</h4>
          <InputLabel>Email:</InputLabel>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="e.g., user@example.com"
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}

          <InputLabel>Password:</InputLabel>
          <Input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your password"
          />
          {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
        </ModalBody>
        <ModalFooter>
        <Button isLoading={isLoading} disabled={isLoading} onClick={handleSubmitForLoggedUsers}>
  {isLoading ? "Logging Job..." : "Log Job"}
</Button>


          <Button flat onClick={() => setShowModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </ModalWrapper>

  </ModalBackdrop>
)}


<InfoMessage>We don't share your personal details.</InfoMessage>

{/* Registration Fields */}
<InputLabel>Name:</InputLabel>
<Input
  type="text"
  name="name"
  value={formData.name}
  onChange={handleChange}
  placeholder="Enter your name"
/>
{errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}

<InputLabel>Phone Number:</InputLabel>
<Input
  type="text"
  name="phone"
  value={formData.phone}
  onChange={handleChange}
  placeholder="Enter your phone number"
/>
{errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}

<InputLabel>Email:</InputLabel>
<Input
  type="email"
  name="email"
  value={formData.email}
  onChange={handleChange}
  placeholder="e.g., user@example.com"
/>
{errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}

<InputLabel>Password:</InputLabel>
<Input
  type="password"
  name="password"
  value={formData.password}
  onChange={handleChange}
  placeholder="Choose your password"
/>
{errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}

<InputLabel>Confirm Password:</InputLabel>
<Input
  type="password"
  name="confirmPassword"
  value={formData.confirmPassword}
  onChange={handleChange}
  placeholder="Confirm your password"
/>
{errors.confirmPassword && <ErrorMessage>{errors.confirmPassword}</ErrorMessage>}

<span>
<Checkbox
  isSelected={formData.termsAccepted}
  onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
>
  
</Checkbox>

    I accept the{" "}
    <TermsLink
      to="/terms-and-conditions"
      state={{
        from: location.pathname,
        formData: formData,
      }}
      onClick={(e) => e.stopPropagation()} // ✅ Stops toggle when clicked
    >
      Terms & Conditions
    </TermsLink>
  </span>

{errors.termsAccepted && <ErrorMessage>{errors.termsAccepted}</ErrorMessage>}

{/* Submit Button */}
<Button isLoading={isLoading} disabled={isLoading} onClick={handleSubmit}>
  {isLoading ? "Logging Job..." : "Log Job"}
</Button>

  </>
)}




        </CardBody>
      </Card>
       
    
  )
}